import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { getAnalytics, logEvent, setUserId } from 'firebase/analytics';

import logo from '../logo-trans.png';
import { useBodyClass, useLangParam } from '../hooks';
import MUI from '../mui';
import { ApiContext } from '../context/Api';
import LanguageSelect from '../components/LanguageSelect';

function SignIn() {
  useLangParam();
  const { apiService } = useContext(ApiContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  useBodyClass('signin');
  useBodyClass('auth-screen');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('userInfo'));
    if (user) {
      navigate('/');
    }
  }, [navigate]);

  async function onSignIn(values) {
    setIsLoading(true);

    apiService
      .sendPostRequest('auth/local', {
        identifier: values.username,
        password: values.password
      })
      .then(response => {
        // Handle success.
        // console.log(response.data.user);
        localStorage.setItem('userInfo', JSON.stringify(response.data));
        const analytics = getAnalytics();
        setUserId(analytics, response.data.user.id);
        logEvent(analytics, 'login', { method: 'strapi' });
        setIsLoading(false);
        navigate('/');
      })
      .catch(error => {
        // Handle error.
        setError(error.response.data.message[0].messages[0].message);
        setIsLoading(false);
      });
  }
  return (
    <MUI mode="dark">
      <Container>
        <div className="top-lang">
          <LanguageSelect showLanguage />
        </div>
        <Stack
          direction="column"
          useFlexGap
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            marginBlockEnd: '1rem',
          }}
        >
          <img
            alt=""
            src={logo}
            width="80"
            className="d-inline-block align-top logo"
          />
          <Typography variant="h4">
            {t('core.progressingTogether')}
          </Typography>
          <Typography variant="h5">{t('auth.signIn')}</Typography>
        </Stack>
        <Formik
          initialValues={{ username: '', password: '' }}
          validationSchema={Yup.object({
            username: Yup.string().required(t('core.required')),
            password: Yup.string().required(t('core.required'))
          })}
          onSubmit={(values, { setSubmitting }) => {
            onSignIn(values);
            setSubmitting(false);
          }}
        >
          {({ handleChange, errors, touched }) => (
            <Form className="form-base">
              <Stack direction="column" useFlexGap={true} spacing={2}>
                <TextField
                  label={t('auth.username') + '*'}
                  className="form-field"
                  type="text"
                  name="username"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'username') &&
                      getIn(errors, 'username')
                  )}
                  helperText={
                    getIn(touched, 'username') &&
                    getIn(errors, 'username')
                      ? getIn(errors, 'username')
                      : ''
                  }
                />
                <TextField
                  label={t('auth.password') + '*'}
                  className="form-field"
                  type="password"
                  name="password"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'password') &&
                      getIn(errors, 'password')
                  )}
                  helperText={
                    getIn(touched, 'password') &&
                    getIn(errors, 'password')
                      ? getIn(errors, 'password')
                      : ''
                  }
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  color="dark"
                  size="large"
                  loading={isLoading}
                  sx={{ width: '100%', fontSize: '1.25rem' }}
                >
                  {t('auth.signIn')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
        <div className="end-text text-center">
          <Link to="../signup">
            {t('auth.createAccount')}
          </Link>
        </div>
        <div className="end-text text-center">
          <Link to="../forgot-password">
            {t('auth.forgotPasswordPrompt')}
          </Link>
        </div>
        <div className="end-text text-center" style={{marginTop: '15%'}}>
          <Button
            onClick={() => navigate('../')}
            variant="outlined"
            size="large"
            color="white"
            sx={{ width: '100%', fontSize: '1.25rem', fontWeight: 'normal' }}
          >
            {t('auth.continueWithoutSignIn')}
          </Button>
        </div>
      </Container>
    </MUI>
  );
}
export default SignIn;
